import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { http } from '../../services/HttpInterceptor';
import {
    RouteComponentProps, Link
} from "react-router-dom";
import { toast } from 'react-toastify';
import { observable } from 'mobx';
import DatePicker from "react-datepicker";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { sessionService } from '../../services/SessionService';
import { apiUrl } from '../../services/HttpInterceptorUtil';

registerLocale('es', es);
setDefaultLocale('es');

interface DownloadFacturasProps extends RouteComponentProps {
    changeToggle(action?: boolean): void
}

@observer export default class DownloadFacturas extends React.Component<DownloadFacturasProps> {

    @observable private fromDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    @observable private toDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    @observable private isLoaded = false;
    @observable private loading = false;
    @observable private facturas: any[] = [];
    @observable private facturasData: any = null;
    @observable private selectedFacturas: Set<string> = new Set();
    @observable private downloadingBulk = false;

    componentDidMount() {
        this.props.changeToggle(true);
        // Check if user has permission to view facturas
        if (!sessionService.canViewSection("Facturas.view")) {
            toast.error("No tienes permisos para acceder a esta sección");
            this.props.history.push("/facturas");
            return;
        }
        this.isLoaded = true;
    }

    setStartDate = (date: any) => {
        this.fromDate = date;
    }

    setEndDate = (date: any) => {
        this.toDate = date;
    }

    searchFacturas = () => {
        this.loading = true;
        this.selectedFacturas.clear(); // Clear selections when searching
        
        const fromDateStr = format(this.fromDate, 'yyyy-MM-dd\'T\'HH:mm:ss');
        const toDateStr = format(this.toDate, 'yyyy-MM-dd\'T\'HH:mm:ss');
        
        const url = `/api/controllers/dashboard/read_facturas.php?date_from=${fromDateStr}&date_to=${toDateStr}`;
        
        http.get(url)
            .then((response) => {
                if (response.data.success) {
                    this.facturasData = response.data;
                    this.facturas = response.data.invoices || [];
                    toast.success(`Se encontraron ${response.data.total_invoices} facturas para el período seleccionado`);
                } else {
                    toast.error("Error al obtener las facturas");
                }
            })
            .catch((error) => {
                console.error('Error loading facturas:', error);
                toast.error("Error al cargar las facturas");
            })
            .finally(() => {
                this.loading = false;
            });
    }

    downLoadInvoice = (paymentId: any, uuid: any): void => {
        window.open(`${apiUrl().url.API_URL}/api/payment/factura_download/${paymentId}/${uuid}`, "_blank");
    }

    toggleFacturaSelection = (facturaKey: string) => {
        const newSelected = new Set(this.selectedFacturas);
        if (newSelected.has(facturaKey)) {
            newSelected.delete(facturaKey);
        } else {
            newSelected.add(facturaKey);
        }
        this.selectedFacturas = new Set(newSelected);
    }

    selectAllFacturas = () => {
        // Select all facturas using index as key
        const allKeys = this.facturas.map((_, index) => `factura-${index}`);
        this.selectedFacturas = new Set(allKeys);
    }

    deselectAllFacturas = () => {
        this.selectedFacturas = new Set();
    }

    downloadSelectedFacturas = async () => {
        if (this.selectedFacturas.size === 0) {
            toast.warning("Por favor selecciona al menos una factura para descargar");
            return;
        }

        this.downloadingBulk = true;

        try {
            // Prepare the data for bulk download
            const selectedData = Array.from(this.selectedFacturas).map(key => {
                const index = parseInt(key.replace('factura-', ''));
                const factura = this.facturas[index];
                return { 
                    payment_id: factura.payment_id || factura.id || index, 
                    uuid: factura.uuid || factura.invoice_uuid || `factura-${index}`,
                    index: index
                };
            });

            // Use AJAX to get the download URL
            const response = await http.post('/api/payment/bulk_factura_download', {
                facturas: selectedData
            }, {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bulk_facturas_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`Descarga completada: ${this.selectedFacturas.size} facturas descargadas`);
        } catch (error) {
            console.error('Error downloading bulk facturas:', error);
            
            // Try fallback method if AJAX fails
            try {
                const selectedData = Array.from(this.selectedFacturas).map(key => {
                    const index = parseInt(key.replace('factura-', ''));
                    const factura = this.facturas[index];
                    return { 
                        payment_id: factura.payment_id || factura.id || index, 
                        uuid: factura.uuid || factura.invoice_uuid || `factura-${index}`,
                        index: index
                    };
                });

                // Create a temporary form to submit the data
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = `${apiUrl().url.API_URL}/api/payment/bulk_factura_download`;
                form.target = '_blank';

                // Add the data as hidden inputs
                selectedData.forEach((item, index) => {
                    const paymentInput = document.createElement('input');
                    paymentInput.type = 'hidden';
                    paymentInput.name = `facturas[${index}][payment_id]`;
                    paymentInput.value = item.payment_id;
                    form.appendChild(paymentInput);

                    const uuidInput = document.createElement('input');
                    uuidInput.type = 'hidden';
                    uuidInput.name = `facturas[${index}][uuid]`;
                    uuidInput.value = item.uuid;
                    form.appendChild(uuidInput);
                });

                // Submit the form
                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);

                toast.success(`Descargando ${this.selectedFacturas.size} facturas...`);
            } catch (fallbackError) {
                console.error('Fallback download also failed:', fallbackError);
                toast.error("Error al descargar las facturas seleccionadas");
            }
        } finally {
            this.downloadingBulk = false;
        }
    }

    render() {
        if (!this.isLoaded) {
            return null;
        }

        // All facturas are now selectable
        const hasFacturas = this.facturas.length > 0;

        return (
            <Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header card-header-primary">
                                <h4 className="card-title">Lista de Facturas</h4>
                                <p className="card-category">Consulta facturas por rango de fechas</p>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        Desde: <DatePicker selected={this.fromDate} onChange={this.setStartDate} />
                                        Hasta: <DatePicker selected={this.toDate} onChange={this.setEndDate} />
                                    </div>
                                    <div className="col-md-4">
                                        <button 
                                            className="btn btn-info btn-sm"
                                            onClick={this.searchFacturas}
                                            disabled={this.loading}
                                        >
                                            <i className="material-icons">search</i> Buscar
                                            <div className="ripple-container"></div>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-12">
                                        <Link to="/facturas">
                                            <button className="btn btn-secondary btn-lg">
                                                <i className="material-icons">arrow_back</i>
                                                Volver
                                            </button>
                                        </Link>
                                    </div>
                                </div>

                                {this.facturasData && (
                                    <div className="row mt-3">
                                        <div className="col-md-12">
                                            <div className="alert alert-info">
                                                <h6>Resumen de la consulta:</h6>
                                                <ul>
                                                    <li><strong>Total de facturas:</strong> {this.facturasData.total_invoices}</li>
                                                    <li><strong>Período:</strong> {this.facturasData.date_from} - {this.facturasData.date_to}</li>
                                                    <li><strong>RFC:</strong> {this.facturasData.rfc}</li>
                                                    <li><strong>Tipo de factura:</strong> {this.facturasData.invoice_type}</li>
                                                    <li><strong>Ambiente:</strong> {this.facturasData.environment}</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {this.facturas.length > 0 && (
                                    <div className="row mt-3">
                                        <div className="col-md-12">
                                            {/* Bulk Download Controls */}
                                            {hasFacturas && (
                                                <div className="row mb-3">
                                                    <div className="col-md-12">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <button 
                                                                    className="btn btn-outline-primary btn-sm me-2"
                                                                    onClick={this.selectAllFacturas}
                                                                >
                                                                    <i className="material-icons">select_all</i> Seleccionar Todas
                                                                </button>
                                                                <button 
                                                                    className="btn btn-outline-secondary btn-sm me-2"
                                                                    onClick={this.deselectAllFacturas}
                                                                >
                                                                    <i className="material-icons">clear_all</i> Deseleccionar Todas
                                                                </button>
                                                                <span className="text-muted">
                                                                    {this.selectedFacturas.size} de {this.facturas.length} facturas seleccionadas
                                                                </span>
                                                            </div>
                                                            <button 
                                                                className="btn btn-success"
                                                                onClick={this.downloadSelectedFacturas}
                                                                disabled={this.selectedFacturas.size === 0 || this.downloadingBulk}
                                                            >
                                                                <i className="material-icons">
                                                                    {this.downloadingBulk ? 'hourglass_empty' : 'download'}
                                                                </i>
                                                                {this.downloadingBulk ? 'Descargando...' : `Descargar ${this.selectedFacturas.size} facturas`}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="table-responsive">
                                                <table className="table">
                                                    <thead className="text-primary">
                                                        <tr>
                                                            <th>Seleccionar</th>
                                                            <th>Fecha</th>
                                                            <th>UUID</th>
                                                            <th>RFC</th>
                                                            <th>Tipo de Factura</th>
                                                            <th>Payment ID</th>
                                                            <th className="text-right">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.facturas.map((factura: any, index: any) => {
                                                            const facturaKey = `factura-${index}`;
                                                            const isSelected = this.selectedFacturas.has(facturaKey);
                                                            
                                                            return (
                                                                <tr key={index}>
                                                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={() => this.toggleFacturaSelection(facturaKey)}
                                                                                className="form-check-input"
                                                                                style={{ 
                                                                                    width: '16px', 
                                                                                    height: '16px', 
                                                                                    cursor: 'pointer',
                                                                                    margin: '0',
                                                                                    padding: '0'
                                                                                }}
                                                                            />
                                                                            <small style={{ fontSize: '10px', color: '#666', lineHeight: '1' }}>
                                                                                {isSelected ? '✓' : '☐'}
                                                                            </small>
                                                                        </div>
                                                                    </td>
                                                                    <td>{factura.date ? format(new Date(factura.date), 'dd/MM/yyyy HH:mm') : 'N/A'}</td>
                                                                    <td>{factura.uuid || 'N/A'}</td>
                                                                    <td>{factura.rfc || 'N/A'}</td>
                                                                    <td>{factura.invoice_type || 'N/A'}</td>
                                                                    <td>
                                                                        {factura.payment_id ? (
                                                                            factura.payment_id
                                                                        ) : (
                                                                            <span className="text-danger">
                                                                                <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>cancel</i>
                                                                                Cancelado
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="td-actions text-right">
                                                                        {factura.payment_id && factura.uuid && (
                                                                            <button
                                                                                onClick={() => this.downLoadInvoice(factura.payment_id, factura.uuid)}
                                                                                style={{ margin: 5 }}
                                                                                type="button"
                                                                                className="btn btn-info"
                                                                            >
                                                                                <i className="material-icons">
                                                                                    download
                                                                                </i>
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {this.loading && (
                                    <div className="row mt-3">
                                        <div className="col-md-12 text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Cargando...</span>
                                            </div>
                                            <p>Cargando facturas...</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
} 